import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUser } from '../../store/slices/authSlice';
import { navigationConfig } from '../../config/navigation.jsx';
import { theme } from '../../config/theme';
import { XIcon } from '../icons/Icons';
import { FaStore } from 'react-icons/fa';

const Sidebar = ({ isMobile, isOpen, onClose }) => {
  const user = useSelector(selectUser);
  const menuItems = navigationConfig[user?.role] || [];
  const navigate = useNavigate();

  const getThemeVars = React.useCallback((role) => {
    switch (role) {
      case 'admin':
        return {
          bg: theme.colors.gray[900],
          border: theme.colors.gray[800],
          text: theme.colors.gray[400],
          textHover: theme.colors.white,
          textActive: theme.colors.white,
          bgActive: theme.colors.gray[800],
          hover: theme.colors.gray[800],
          logo: theme.colors.white,
          closeBtn: theme.colors.gray[400],
        };
      case 'seller':
        return {
          bg: '#1e1b4b', // Deep indigo base for seller
          border: '#312e81',
          text: '#a5b4fc',
          textHover: '#ffffff',
          textActive: '#ffffff',
          bgActive: '#312e81',
          hover: '#312e81',
          logo: '#818cf8',
          closeBtn: '#a5b4fc',
        };
      default:
        // Customer or fallback
        return {
          bg: theme.colors.white,
          border: theme.colors.gray[200],
          text: theme.colors.text.secondary,
          textHover: theme.colors.text.primary,
          textActive: theme.colors.primary.main,
          bgActive: `${theme.colors.primary.main}10`,
          hover: theme.colors.gray[50],
          logo: theme.colors.primary.main,
          closeBtn: theme.colors.text.secondary,
        };
    }
  }, []);

  const tVars = React.useMemo(() => getThemeVars(user?.role), [user?.role, getThemeVars]);

  const sidebarStyles = {
    position: 'fixed',
    top: isMobile ? 0 : '64px',
    left: 0,
    width: '250px',
    height: isMobile ? '100vh' : 'calc(100vh - 64px)',
    backgroundColor: tVars.bg,
    borderRight: `1px solid ${tVars.border}`,
    overflowY: 'auto',
    padding: theme.spacing.lg,
    transition: 'transform 0.3s ease-in-out',
    transform: isMobile ? (isOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
    zIndex: theme.zIndex.dropdown + 1, // Sit above navbar and overlay on mobile
  };

  const linkStyles = (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    borderRadius: theme.borderRadius.md,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.medium,
    color: isActive ? tVars.textActive : tVars.text,
    backgroundColor: isActive ? tVars.bgActive : 'transparent',
    textDecoration: 'none',
    marginBottom: theme.spacing.xs,
    transition: theme.transitions.fast,
  });

  return (
    <aside style={sidebarStyles}>
      {isMobile && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.xl }}>
          <div onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <span style={{ fontSize: theme.typography.fontSize.xl, fontWeight: theme.typography.fontWeight.bold, color: tVars.logo }}>
            AlarcTrade
          </span>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: theme.spacing.xs }}
          >
            <XIcon size={24} color={tVars.closeBtn} />
          </button>
        </div>
      )}
      <nav>
        {menuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            style={({ isActive }) => linkStyles(isActive)}
            onMouseEnter={(e) => {
              if (!e.currentTarget.classList.contains('active')) {
                e.currentTarget.style.backgroundColor = tVars.hover;
                e.currentTarget.style.color = tVars.textHover;
              }
            }}
            onMouseLeave={(e) => {
              if (!e.currentTarget.classList.contains('active')) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = tVars.text;
              }
            }}
          >
            <span>{item.icon}</span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </aside>
  );
};

export default Sidebar;   